/**
 * Reading aloud, over the browser's own speech synthesis.
 *
 * No key, no network, no vendor: `speechSynthesis` is in every current browser
 * and the voices come from the operating system. That also sets the honest
 * expectations — the voice list is whatever the machine has installed, quality
 * varies between a Mac and a Windows terminal, and a language with no installed
 * voice simply has none. None of that is worth hiding behind a spinner, so
 * `availability()` reports which of those situations the operator is in.
 *
 * Two behaviours worth naming, both learned from how badly TTS usually behaves
 * in an application:
 *
 *  - Speaking replaces speaking. Pressing read-aloud on a second answer stops
 *    the first rather than queueing behind it, because an operator pressing it
 *    again means "this one", not "this one in ninety seconds".
 *
 *  - Nothing speaks on arrival. Every call here is the result of a press or of
 *    a preference the operator turned on themselves.
 */
import { voiceLog } from "./listen";

export type VoiceAvailability =
  { state: "unsupported" } | { state: "no-voices" } | { state: "ready"; voices: SpeechSynthesisVoice[] };

export function isSupported(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
}

/**
 * The voice list arrives asynchronously in Chrome — synchronously empty on the
 * first call, then filled after `voiceschanged`. A caller that reads it once
 * gets an empty list and concludes the machine has no voices, which is the
 * usual reason a voice picker ships broken.
 */
export function whenVoicesReady(timeoutMs = 1500): Promise<SpeechSynthesisVoice[]> {
  if (!isSupported()) return Promise.resolve([]);
  const now = window.speechSynthesis.getVoices();
  if (now.length > 0) return Promise.resolve(now);

  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.speechSynthesis.removeEventListener("voiceschanged", finish);
      resolve(window.speechSynthesis.getVoices());
    };
    window.speechSynthesis.addEventListener("voiceschanged", finish);
    window.setTimeout(finish, timeoutMs);
  });
}

export async function availability(): Promise<VoiceAvailability> {
  if (!isSupported()) return { state: "unsupported" };
  const voices = await whenVoicesReady();
  if (voices.length === 0) return { state: "no-voices" };
  return { state: "ready", voices };
}

export interface SpeakOptions {
  voiceURI?: string;
  rate?: number;
  pitch?: number;
  onStart?: () => void;
  onEnd?: () => void;
}

/**
 * Strips the parts of an answer that are furniture rather than words. Reading
 * "bullet W O dash zero zero zero zero one two" aloud helps nobody, and the
 * source chips underneath are for the eye.
 */
export function speakableText(text: string): string {
  return (
    text
      .split("\n")
      .map((line) => line.replace(/^[•\-*]\s*/, "").trim())
      .filter(Boolean)
      .join(". ")
      /* A figure is read without an "s" stuck to it: "8s", "P1s", "1,200's"
       came out as "eights", "P ones". Said only — the screen keeps the text. */
      .replace(/(\d)['’]?s\b/g, "$1")
      .replace(/\s+/g, " ")
      .slice(0, 1200)
  );
}

/** About three sentences aloud; a longer answer is on the screen. */
export const SPOKEN_LIMIT = 420;

/**
 * What she says aloud of an answer: all of a short one, and of a long one
 * the sentences that fit, then where the rest is. Reading a whole board out
 * is not an answer to a question, and it was heard as her not stopping.
 */
export function spokenPart(text: string, limit = SPOKEN_LIMIT): string {
  const all = speakableText(text);
  if (all.length <= limit) return all;
  const cut = all.slice(0, limit);
  const end = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("? "), cut.lastIndexOf("! "));
  return `${end > 80 ? cut.slice(0, end + 1) : cut.replace(/\s+\S*$/, "") + "…"} The rest is on your screen.`;
}

/**
 * What language to transcribe the microphone as.
 *
 * Three sources, in the order an operator would expect them to win: what they
 * chose explicitly, then the language of the voice they picked for her — if
 * she answers in American English it is a fair guess they are speaking it —
 * and finally the browser's own language. `en-GB` is the last resort rather
 * than the assumption it used to be.
 *
 * Kept here because this is the module that can see the installed voices.
 */
export function recognitionLang(chosen: string, voiceURI: string): string {
  if (chosen) return chosen;
  if (voiceURI && isSupported()) {
    const v = window.speechSynthesis.getVoices().find((x) => x.voiceURI === voiceURI);
    if (v?.lang) return v.lang;
  }
  if (typeof navigator !== "undefined" && navigator.language) return navigator.language;
  return "en-GB";
}

export function stop(): void {
  if (!isSupported()) return;
  /* a sentence cut off must not "end" later, on its watchdog */
  window.clearTimeout(watchdog);
  current = null;
  try {
    window.speechSynthesis.cancel();
  } catch {
    /* a browser that refuses to stop speaking is not something we can fix from here */
  }
}

/*
 * The utterance being spoken, held here on purpose.
 *
 * Safari lets go of an utterance nothing else refers to, and one it has let
 * go of never reports that it ended — so everything waiting on "she has
 * finished speaking" (listening for the next question, above all) waited for
 * ever. Holding it, and a watchdog timed from its length, are the two halves
 * of the usual cure.
 */
let current: SpeechSynthesisUtterance | null = null;
let watchdog = 0;

export function speak(text: string, opts: SpeakOptions = {}): boolean {
  if (!isSupported()) return false;
  const body = spokenPart(text);
  if (!body) return false;

  stop(); // this one, not this one in ninety seconds
  try {
    const u = new SpeechSynthesisUtterance(body);
    current = u;
    let ended = false;
    const finish = (why: string) => {
      if (ended) return;
      ended = true;
      /* A sentence cut off can report its end after the next one has started;
         its watchdog is that sentence's, not this one's. */
      if (current === u) {
        window.clearTimeout(watchdog);
        current = null;
      }
      voiceLog(`speech: ${why}`);
      opts.onEnd?.();
    };
    if (opts.voiceURI) {
      const v = window.speechSynthesis.getVoices().find((x) => x.voiceURI === opts.voiceURI);
      if (v) u.voice = v;
    }
    u.rate = opts.rate ?? 1;
    u.pitch = opts.pitch ?? 1;
    u.onstart = () => {
      voiceLog(`speech: start "${body.slice(0, 40)}"`);
      opts.onStart?.();
    };
    u.onend = () => finish("end");
    u.onerror = (e) => finish(`error ${(e as SpeechSynthesisErrorEvent).error ?? "?"}`);
    window.speechSynthesis.speak(u);
    /* about fourteen characters a second at rate 1, and room to spare */
    const expected = ((body.length / 14) * 1000) / (u.rate || 1) + 3000;
    window.clearTimeout(watchdog);
    watchdog = window.setTimeout(() => finish("end not reported by the browser; moving on"), expected);
    return true;
  } catch {
    return false;
  }
}
